import React, { useMemo, useState } from "react";
import {
  Button,
  Divider,
  DropdownItemProps,
  Form,
  Grid,
  Icon,
  Label,
  Message,
  Table,
} from "semantic-ui-react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { ErrorMessage, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import MyIconDecimalInput from "../../app/common/form/MyIconDecimalInput";
import MySelectInput from "../../app/common/form/MySelectInput";
import {
  getNegative,
  formatDecimal,
  round2Decimals,
  formatCurrency,
} from "../../app/common/util/functions";
import PageHeader from "../../app/layout/PageHeader";
import { SettingsPayType } from "../../app/models/pricing";
import { useStore } from "../../app/stores/store";
import ValidationErrors from "../errors/ValidationErrors";
import { format } from "date-fns";
import MyPaging from "../../app/common/form/MyPaging";
import { PagingParams } from "../../app/models/pagination";
import { ApplyARToOutstandingTicketFormValues, ApplyARToOutstandingTicketRequestItemDto } from "../../app/models/accountsReceivable";
import MyTextInput from "../../app/common/form/MyTextInput";

interface Props {
  customerId?: string;
  dropdownItems: DropdownItemProps[];
  addItem?: (values: ApplyARToOutstandingTicketFormValues) => void;
  amountDue: number;
  isAdd: boolean;
}

export default observer(function ARPaymentForm({
  customerId,
  dropdownItems,
  addItem,
  amountDue,
  isAdd,
}: Props) {
  const { t } = useTranslation(["common", "translation", "tickets"]);
  const { accountsReceivableStore, accountsReceivableIndexStore, modalStore, pricingStore, customerStore } = useStore();
  const { payTypeRegistry } = pricingStore;
  const { loadARIndex } = accountsReceivableIndexStore;
  const {
    getPaymentTicketsModal,
    paymentTicketsModalAR,
    paymentTicketsModalPagination,
    resetPaymentTicketsModalRegistry,
    paymentTicketsModalPagingParams,
    setPaymentTicketsModalPagingParams,
    loadingInitial,
    loadPaymentTicketsModal,
    payForTickets,
  } = accountsReceivableStore;

  const {
    loadAccountsReceivableForCustomerId
  } = customerStore;

  const [overage, setOverage] = useState<number | undefined>(undefined);
  const [amountToPayAmt, setAmountToPayAmt] = useState<number | undefined>(undefined);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const totalAmountDue = useMemo(() => amountDue, [getPaymentTicketsModal, customerId]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const calcOverage = (value: number | undefined) => {
    if (value) {
      const diff: number = totalAmountDue - Number(value);
      setOverage(diff < 0 ? diff : undefined);
    } else {
      setOverage(undefined);
    }
  };

  const validationSchema = Yup.object({
    paymentTypeId: Yup.string().required(
      t("paymentProcessing.payMethod", { ns: "errors" })
    ),
    referenceNumber: Yup.string().nullable()
  });

  function handleFormSubmit(values: ApplyARToOutstandingTicketFormValues, { setSubmitting, setFieldValue }: FormikHelpers<ApplyARToOutstandingTicketFormValues>) {
    setSubmitting(true);
    let paymentType: SettingsPayType | undefined = payTypeRegistry.get(
      values.paymentTypeId
    );
    values.paymentType = paymentType;

    payForTickets(values)
      .then(() => {
        setFieldValue('totalPaymentAmount', 0);
        if (addItem)
          return addItem(values);
      })
      .then(() => loadARIndex())
      .then(() => loadAccountsReceivableForCustomerId(customerId!))
      .then(() => modalStore.closeModal())
      .finally(() => setSubmitting(false));
  }

  function listOnClick(value: number) {
    if (value > 0 && customerId && paymentTicketsModalPagination && value < paymentTicketsModalPagination?.totalPages + 1) {
      setPaymentTicketsModalPagingParams(new PagingParams(value));
      resetPaymentTicketsModalRegistry();
      loadPaymentTicketsModal(customerId);
    }
  }

  return (
    <>
      <PageHeader
        type={"h3"}
        header={`${isAdd
          ? t("add", {
            ns: "common",
          })
          : t("edit", {
            ns: "common",
          })
          } ${t("payment", {
            ns: "tickets",
          })}`}
        divider={true}
        addTitle={false}
      />
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          customerId: customerId,
          accountsReceivableId: paymentTicketsModalAR?.id ?? "",
          referenceNumber: paymentTicketsModalAR?.accountNumber ?? "",
          totalPaymentAmount: undefined,
          paymentTypeId: undefined,
          items: (getPaymentTicketsModal ?? []).map((pay) => ({
            ...pay,
            amountToPay: 0
          } as ApplyARToOutstandingTicketRequestItemDto)),
        } as ApplyARToOutstandingTicketFormValues}
        onSubmit={(values, helpers) => {
          handleFormSubmit(values, helpers)
        }}
      >
        {({
          handleSubmit,
          isValid,
          isSubmitting,
          dirty,
          errors,
          values,
          setFieldValue
        }) => (
          <Form
            className="ui form error"
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            <ErrorMessage
              name="error"
              render={() => <ValidationErrors errors={errors.error} />}
            />
            {errors.error}
            <Grid stackable>
              <Grid.Row columns={2} key="paymentType.amountToPay">
                <Grid.Column verticalAlign="top">
                  <MySelectInput
                    name={"paymentTypeId"}
                    options={dropdownItems}
                    placeholder={""}
                    clearable={false}
                    label={`${t("estimate.payment_method", {
                      ns: "tickets",
                    })}:`}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row className='align-items-end' columns={2} key="referenceNumber.amountDue">
                <Grid.Column verticalAlign="middle">
                  <MyTextInput
                    name="referenceNumber"
                    label={`${t("referenceNumber", {
                      ns: "accountsReceivable",
                    })}:`}
                    placeholder={`${t("referenceNumber", {
                      ns: "accountsReceivable",
                    })}`}
                    maxLength={500}
                  />
                </Grid.Column>
                <Grid.Column verticalAlign="top">
                  <label className='myLabel'>
                    {t("estimate.amountDue", {
                      ns: "tickets",
                    }) + ":"}
                  </label>
                  <Grid.Row className='align-items-end w-100'>
                    <Label
                      basic
                      simple
                      fluid
                      className="myLabel longInput labelHeight"
                      style={{
                        color: getNegative(amountDue ?? 0),
                      }}
                    >
                      <Icon name="dollar" />
                      {formatDecimal(round2Decimals(amountDue ?? 0) ?? 0, 2)}
                    </Label>
                  </Grid.Row>
                </Grid.Column>
              </Grid.Row>
              {overage && (
                <Grid.Row columns={2} key={"overage"}>
                  <Grid.Column verticalAlign="middle" textAlign="right">
                    {t("estimate.changeDue", {
                      ns: "tickets",
                    })}
                    :
                  </Grid.Column>
                  <Grid.Column
                    style={{
                      color: getNegative(overage ?? 0),
                    }}
                  >
                    {formatCurrency(overage ?? 0)}
                  </Grid.Column>
                </Grid.Row>
              )}
              <Divider />
              <Grid.Row key="ticketsToPay">
                <Table striped unstackable className="dnxTable no_padding">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>
                        {t("ticketnum", { ns: "common" })}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t("date", { ns: "common" })}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t("description", { ns: "common" })}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t("labels.ticketTotal", { ns: "inventory" })}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t("balanceDue", { ns: "accountsReceivable" })}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t("amountToPayOnTicket", { ns: "accountsReceivable" })}
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {getPaymentTicketsModal && getPaymentTicketsModal.length
                      ? getPaymentTicketsModal.map((pm, index) => (
                        <Table.Row key={pm.ticketId}>
                          <Table.Cell
                            data-label={`${t("ticketnum", { ns: "common" })}:`}
                          >
                            #{pm.ticketNumber}
                          </Table.Cell>
                          <Table.Cell
                            data-label={`${t("date", { ns: "common" })}:`}
                          >
                            {pm.ticketDate ? format(new Date(pm.ticketDate), 'P') : null}
                          </Table.Cell>
                          <Table.Cell
                            data-label={`${t("description", { ns: "common" })}:`}
                          >
                            {pm.description}
                          </Table.Cell>
                          <Table.Cell
                            data-label={`${t("labels.ticketTotal", { ns: "inventory" })}:`}
                          >
                            {formatCurrency(pm.ticketTotal)}
                          </Table.Cell>
                          <Table.Cell
                            data-label={`${t("balanceDue", { ns: "accountsReceivable" })}:`}
                          >
                            {formatCurrency(pm.balanceDue)}
                          </Table.Cell>
                          <Table.Cell
                            width={4}
                            data-label={`${t("paymentAmount", { ns: "tickets" })}:`}
                          >
                            <MyIconDecimalInput
                              icon="dollar sign"
                              iconPosition="left"
                              placeholder="0.00"
                              name={`amountToPay_${pm.ticketId}`}
                              value={(values.items[index]?.amountToPay || 0).toString()}
                              onChange={(d) => {

                                const newPayment = parseFloat(d ? d : 0); // Convert input to a number
                                const totalPayAmount = values.totalPaymentAmount ? values.totalPaymentAmount : 0;
                                if (isNaN(newPayment)) {
                                  console.error("Invalid payment amount:", d);
                                  return;
                                }

                                // Create a copy of the items array to avoid direct mutation
                                const updatedItems = [...values.items];

                                // Ensure the current item exists or initialize it
                                if (!updatedItems[index]) {
                                  updatedItems[index] = { amountToPay: 0 } as ApplyARToOutstandingTicketRequestItemDto;
                                }

                                // Calculate the remaining amount and total payment
                                const currentItem = updatedItems[index];
                                const remainingAmount = totalPayAmount - currentItem.amountToPay;
                                const totalPayment = Number((remainingAmount + newPayment).toFixed(2));

                                // Validate if the total payment is within the allowed amount
                                if (newPayment <= pm.balanceDue) {
                                  // Update the current item's amountToPay
                                  currentItem.amountToPay = newPayment;

                                  // Update the form state
                                  setFieldValue("items", updatedItems);
                                  setFieldValue("totalPaymentAmount", totalPayment);
                                  setAmountToPayAmt(totalPayment);
                                } else {
                                  console.warn("Payment exceeds the allowed amount.");
                                }
                              }}
                              maxLength={20}
                            />
                          </Table.Cell>
                        </Table.Row>
                      ))
                      : (
                        <Table.Row key={"noItems"}>
                          <Table.Cell width="6" colSpan={6}>
                            <Message
                              color="yellow"
                              className="textAlignCenter fontStyleItalic"
                            >
                              {t("grid_noItems", { ns: "common" })}
                            </Message>
                          </Table.Cell>
                        </Table.Row>
                      )}

                  </Table.Body>
                  <Table.Footer>
                    <Table.Row>
                      <Table.Cell colSpan="6">
                        <MyPaging
                          itemCount={paymentTicketsModalPagination ? paymentTicketsModalPagination.totalItems : 25}
                          pageSize={paymentTicketsModalPagingParams.pageSize}
                          currentPage={paymentTicketsModalPagination ? paymentTicketsModalPagination.currentPage : 1}
                          onClick={listOnClick}
                          onChange={listOnClick}
                          floated="right"
                        ></MyPaging>
                      </Table.Cell>
                    </Table.Row>
                  </Table.Footer>
                </Table>
              </Grid.Row>
              <Grid.Row columns={2}>
                <Grid.Column></Grid.Column>
                <Grid.Column verticalAlign="top">
                  <label className={"myLabel"}>
                    {t("paymentAmount", { ns: "tickets" })}:
                  </label>
                  <br />
                  <Label
                    basic
                    simple
                    fluid
                    className="myLabel labelHeight w-100"
                  >
                    <Icon name="dollar" />
                    {formatDecimal(round2Decimals(amountToPayAmt ?? 0) ?? 0, 2)}

                  </Label>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row key="callToAction">
                <Grid.Column>
                  <Button
                    color="green"
                    className="save_button"
                    icon="check"
                    content={t("save", { ns: "common" })}
                    disabled={isSubmitting || !dirty || !(values.items.some(itm => itm.amountToPay > 0))}
                    loading={isSubmitting || loadingInitial}
                    type="submit"
                  >
                    {`${t("save", {
                      ns: "common",
                    })} ${t("payment", {
                      ns: "tickets",
                    })}`}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      modalStore.closeModal();
                    }}
                  >
                    <Icon name="x" />
                    {t("cancel", { ns: "common" })}
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
});
